<script setup lang="ts">
import { VForm } from "vuetify/components/VForm";
import { VNodeRenderer } from "@layouts/components/VNodeRenderer";
import { themeConfig } from "@themeConfig";
import BordexLogo from "@images/bordex-logo.svg?component";
import { UserCredential } from "firebase/auth";
import { AuthUser } from "@/data/entities/user_profile/auth_user";

definePage({
  meta: {
    layout: "blank",
    unauthenticatedOnly: true,
  },
});

const route = useRoute();
const router = useRouter();
const ability = useAbility();

const isPasswordVisible = ref(false);
const loading = ref(false);
const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
});
const refVForm = ref<VForm>();
const credentials = ref({
  email: "",
  password: "",
});

function setCookies(
  userData: UserCredential,
  claims: Record<string, any>,
  accessToken: string,
) {
  useCookie("accessToken").value = accessToken;

  const userAbilityRules = AuthUser.ClaimsToRawAbilityRules(claims);
  ability.update(userAbilityRules as any);
  localStorage.setItem("userAbilityRules", JSON.stringify(userAbilityRules));

  useCookie("userData").value = JSON.stringify({
    id: userData.user.uid,
    fullName: userData.user.displayName ?? "Onbekend",
    username: userData.user.email ?? "admin",
    avatar: `${import.meta.env.BASE_URL ?? "/"}images/avatars/avatar-1.png`,
    email: userData.user.email,
  });
}

async function login(): Promise<void> {
  loading.value = true;

  const result = await AuthUser.SignIn(
    credentials.value.email,
    credentials.value.password,
  ).catch((error) => {
    const errorCode = error.code;
    const errorMessage = error.message;
    errors.value = {
      email: errorCode + ": " + errorMessage,
      password: errorCode + ": " + errorMessage,
    };
    loading.value = false;
  });
  if (!result) {
    return;
  }

  setCookies(result.credentials, result.claims, result.accessToken);

  await nextTick(() => {
    router.replace(route.query.to ? String(route.query.to) : "/dashboard");
  });

  setTimeout(() => {
    loading.value = false;
  }, 500);
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) login();
  });
};
</script>

<template>
  <VRow no-gutters class="auth-wrapper">
    <!-- Left panel: branded -->
    <VCol md="8" class="d-none d-md-flex auth-panel">
      <div class="auth-panel-inner">
        <!-- Bordex logo SVG -->
        <div class="auth-panel-logo-wrap">
          <BordexLogo class="auth-panel-svg" />
        </div>

        <!-- Tagline -->
        <div class="auth-panel-content">
          <h2 class="auth-panel-title">Alles voor jouw logistieke operatie</h2>
          <p class="auth-panel-subtitle">
            Beheer pakbonnen, drukorders, klanten en prijzen op één plek.
          </p>
        </div>

        <!-- Decorative shapes -->
        <div class="auth-panel-shape auth-panel-shape--1" />
        <div class="auth-panel-shape auth-panel-shape--2" />
        <div class="auth-panel-shape auth-panel-shape--3" />
      </div>
    </VCol>

    <!-- Right panel: login form -->
    <VCol cols="12" md="4" class="auth-form-col d-flex align-center justify-center">
      <div class="auth-form-wrapper">
        <!-- Logo (mobile only) -->
        <RouterLink to="/" class="auth-form-logo d-flex d-md-none align-center gap-x-2 mb-8">
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="auth-title">BordexTools</span>
        </RouterLink>

        <div class="mb-8">
          <h4 class="text-h4 font-weight-bold mb-1">Welkom terug</h4>
          <p class="text-medium-emphasis mb-0">{{ $t("Sign in to continue") }}</p>
        </div>

        <VForm ref="refVForm" @submit.prevent="onSubmit">
          <div class="d-flex flex-column gap-4">
            <AppTextField
              v-model="credentials.email"
              :label="$t('Email')"
              placeholder="jij@bordex.nl"
              type="email"
              autofocus
              :rules="[requiredValidator, emailValidator]"
              :error-messages="errors.email"
            />

            <div>
              <AppTextField
                v-model="credentials.password"
                :label="$t('Password')"
                placeholder="············"
                :rules="[requiredValidator]"
                :type="isPasswordVisible ? 'text' : 'password'"
                :error-messages="errors.password"
                :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
              <div class="d-flex justify-end mt-2">
                <RouterLink class="text-primary text-sm" :to="{ name: 'forgot-password' }">
                  {{ $t("Forgot password?") }}
                </RouterLink>
              </div>
            </div>

            <VBtn block type="submit" :loading="loading" size="large" class="mt-2">
              {{ $t("Log in") }}
            </VBtn>
          </div>
        </VForm>
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";

.auth-wrapper {
  min-block-size: 100dvh;
}

// Left branded panel
.auth-panel {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%);
}

.auth-panel-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  block-size: 100%;
  padding: 2.5rem;
  z-index: 1;
}

.auth-panel-logo-wrap {
  display: flex;
  align-items: flex-start;
}

.auth-panel-svg {
  width: 240px;
  height: auto;
}

// Per-element staggered letter animation
.auth-panel-svg .logo-mark,
.auth-panel-svg .logo-ltr,
.auth-panel-svg .logo-sub {
  animation: svgLetterIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.auth-panel-svg .logo-mark  { animation-delay: 0.1s; }
.auth-panel-svg .logo-ltr-1 { animation-delay: 0.28s; }
.auth-panel-svg .logo-ltr-2 { animation-delay: 0.36s; }
.auth-panel-svg .logo-ltr-3 { animation-delay: 0.44s; }
.auth-panel-svg .logo-ltr-4 { animation-delay: 0.52s; }
.auth-panel-svg .logo-ltr-5 { animation-delay: 0.60s; }
.auth-panel-svg .logo-ltr-6 { animation-delay: 0.68s; }
.auth-panel-svg .logo-sub   { animation-delay: 0.82s; }

@keyframes svgLetterIn {
  from {
    opacity: 0;
    transform: translateY(10px);
    transform-box: fill-box;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    transform-box: fill-box;
  }
}

.auth-panel-content {
  padding-block-end: 4rem;
  animation: contentReveal 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.95s both;
}

@keyframes contentReveal {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.auth-panel-title {
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
  margin-block-end: 1rem;
  max-inline-size: 22rem;
}

.auth-panel-subtitle {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.75);
  max-inline-size: 20rem;
  line-height: 1.6;
  margin: 0;
}

// Decorative shapes
.auth-panel-shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
}

.auth-panel-shape--1 {
  inline-size: 420px;
  block-size: 420px;
  inset-block-start: -100px;
  inset-inline-end: -80px;
}

.auth-panel-shape--2 {
  inline-size: 280px;
  block-size: 280px;
  inset-block-end: 80px;
  inset-inline-end: 60px;
  background: rgba(255, 255, 255, 0.05);
}

.auth-panel-shape--3 {
  inline-size: 160px;
  block-size: 160px;
  inset-block-end: -40px;
  inset-inline-start: 60px;
  background: rgba(255, 255, 255, 0.08);
}

// Right form panel
.auth-form-col {
  background: rgb(var(--v-theme-surface));
}

.auth-form-wrapper {
  inline-size: 100%;
  max-inline-size: 420px;
  padding: 2rem;
}

.auth-form-logo {
  text-decoration: none;
}
</style>
